import { DisplayColumnFields, DisplayFieldType } from '@/models/features/display-column.models'
import { UserRole, UserRoleEnum } from '@/models/features/user.models'

import { Person, PersonFieldKey } from './person-column.types'

// Note: modal list order = the order details render in the cell (avatar, name + tags, then the
// " · " line). Resident-only fields of a Mixed column are skipped on staff rows.
export const PERSON_FIELDS_BY_TYPE: Record<DisplayFieldType, PersonFieldKey[]> = {
  mixed: ['avatar', 'name', 'role', 'apartment', 'buildingNumber', 'email', 'phone', 'street'],
  resident: ['avatar', 'name', 'apartment', 'buildingNumber', 'email', 'phone', 'street'],
  staff: ['avatar', 'name', 'role', 'email', 'phone'],
}

export const RESIDENT_ONLY_FIELDS: PersonFieldKey[] = ['apartment', 'buildingNumber', 'street']

// Note: mirrors the BE `DefaultValuesByFieldType` (DisplayColumnService) - used before the
// settings load, when they fail to load, and by "Reset to default".
export const DEFAULT_FIELDS_BY_TYPE: Record<DisplayFieldType, DisplayColumnFields> = {
  mixed: {
    apartment: false,
    avatar: true,
    buildingNumber: false,
    email: true,
    firstName: true,
    lastName: true,
    phone: false,
    role: true,
    street: false,
  },
  resident: {
    apartment: true,
    avatar: true,
    buildingNumber: false,
    email: true,
    firstName: true,
    lastName: true,
    phone: false,
    role: false,
    street: false,
  },
  staff: {
    apartment: false,
    avatar: true,
    buildingNumber: false,
    email: true,
    firstName: true,
    lastName: true,
    phone: false,
    role: true,
    street: false,
  },
}

// Note: matches the BE's `isPropertyManagerV2` policy on PUT /display-column (canManageBuilding).
export const ROLES_ALLOWED_TO_EDIT: UserRole[] = [
  UserRoleEnum.enum.Administrator,
  UserRoleEnum.enum.Manager,
  UserRoleEnum.enum.PropertyManager,
]

// Note: the design's example person (Figma "Column data settings"), previewed when no row on the
// current page has a person to show - e.g. a page of PMS work orders with no ElevateOS creator.
const PLACEHOLDER_RESIDENT: Person = {
  apartment: '1024',
  buildingNumber: '2',
  email: 'cassandra.bellemont@gmail.com',
  firstName: 'Cassandra',
  kind: 'resident',
  lastName: 'Bellemont',
  phone: '(767) 727-7272',
  street: '240 Illinois St',
}

export const PLACEHOLDER_PERSON_BY_TYPE: Record<DisplayFieldType, Person> = {
  mixed: PLACEHOLDER_RESIDENT,
  resident: PLACEHOLDER_RESIDENT,
  staff: {
    email: 'cassandra.bellemont@gmail.com',
    firstName: 'Cassandra',
    kind: 'staff',
    lastName: 'Bellemont',
    phone: '(767) 727-7272',
    role: UserRoleEnum.enum.PropertyManager,
  },
}

export const PERSON_DETAILS_SEPARATOR = ' · '

export const MAX_PREVIEW_SAMPLES = 4

export const HIGHLIGHT_DURATION_MS = 1600

// Note: rough rem budget per rendered piece, so the column is as wide as the details it
// actually shows - not the content of the current page, which would shift the layout while
// loading or paging. The email gets the design's 423px person column (Figma Maintenance
// "Submitted by") minus the avatar and padding, so every default set stays at 42.3rem, while a
// column trimmed to name + a date shrinks to fit. Guarded between 20rem (room for a name and a
// date) and the design's 42.3rem - more details than that are cut off, with the hover tooltip.
export const COLUMN_WIDTH_REM = {
  avatar: 4,
  cellPadding: 3.2,
  email: 35.1,
  leadingDetail: 11.5,
  max: 42.3,
  min: 20,
  name: 14,
  phone: 10,
  roleTag: 11,
  separator: 1.2,
  street: 14,
  tag: 6,
}
